import { ReactNode } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';

interface DetailsAccordionProps {
  defaultOpen?: boolean;
  children: ReactNode;
  variant?: 'default' | 'secondary';
}

const triggerClassNames: Record<NonNullable<DetailsAccordionProps['variant']>, string> = {
  default: 'group bg-green-500 hover:bg-green-600 text-white rounded-lg py-3 px-4 w-full justify-center gap-2 font-semibold text-lg',
  secondary: 'group bg-green-100 hover:bg-green-200 text-green-700 rounded-lg py-3 px-4 w-full justify-center gap-2 font-semibold',
};

const iconClassNames: Record<NonNullable<DetailsAccordionProps['variant']>, string> = {
  default: 'text-white',
  secondary: 'text-green-700',
};

const textClassNames: Record<NonNullable<DetailsAccordionProps['variant']>, string> = {
  default: 'text-white font-semibold',
  secondary: 'text-green-700 font-semibold text-lg',
};

export function DetailsAccordion({ defaultOpen, children, variant = 'default' }: DetailsAccordionProps) {
  return (
    <Accordion defaultValue={defaultOpen ? 'details' : undefined} type="single" collapsible>
      <AccordionItem value="details" className={variant === 'secondary' ? 'group/item' : undefined}>
        <AccordionTrigger
          data-testid={DataTestIdEnum.APPOINTMENT_CARD_TOGGLE_DETAILS}
          underlineOnHover={false}
          classNameIcon={iconClassNames[variant]}
          className={triggerClassNames[variant]}
        >
          <Typography
            as="span"
            variant={TypographyVariant.BODY_TEXT}
            className={`${textClassNames[variant]} group-data-[state=open]:hidden`}
          >
            Ver detalhes
          </Typography>
          <Typography
            as="span"
            variant={TypographyVariant.BODY_TEXT}
            className={`${textClassNames[variant]} hidden group-data-[state=open]:inline`}
          >
            Fechar detalhes
          </Typography>
        </AccordionTrigger>
        {variant === 'secondary' && (
          <div className="h-px bg-gray-200 mt-4 hidden group-data-[state=open]/item:block" />
        )}
        <AccordionContent className="pt-4">
          {children}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
